import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolved: 'light' | 'dark';
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const KEY = 'nxvolt.theme';
const ThemeCtx = createContext<ThemeState | null>(null);

function readPref(): 'light' | 'dark' {
  return typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function apply(t: Theme) {
  const resolved = t === 'system' ? readPref() : t;
  // SEMPRE grava o tema RESOLVIDO, mesmo quando a escolha e `system`.
  //
  // Antes, `system` removia o atributo. Parecia inofensivo -- "sem atributo,
  // manda a media query" -- mas 17 regras do `global.css` sao escritas como
  //
  //     :root[data-theme='dark'] .x,
  //     :root:not([data-theme='light']) .x { ...cores de tema escuro... }
  //
  // FORA de qualquer `@media`. Sem atributo, `:not([data-theme='light'])`
  // casa, e o portal aplicava cores de tema ESCURO para quem esta em
  // `system` com o sistema em CLARO -- que e o padrao de instalacao.
  //
  // No menu isso ficava mascarado porque a barra era escura sempre. Ao fazer
  // o menu seguir o tema, apareceu: texto cinza-claro sobre fundo claro,
  // 1,77:1. Consertar aqui corrige as 17 de uma vez, em vez de reescrever
  // cada regra para dentro de uma media query.
  document.documentElement.setAttribute('data-theme', resolved);
  return resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
    } catch {
      /* storage bloqueado */
    }
    return 'system';
  });

  const [resolved, setResolved] = useState<'light' | 'dark'>(() => apply(theme));

  useEffect(() => {
    setResolved(apply(theme));
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  // reagir a mudança do sistema quando theme = system
  useEffect(() => {
    if (theme !== 'system') return;
    const m = window.matchMedia('(prefers-color-scheme: dark)');
    // Reaplica o ATRIBUTO, nao so o estado de React: com `system`, trocar o
    // tema do sistema operacional com o app aberto precisa reescrever
    // `data-theme`, senao ele fica congelado no valor da montagem e as
    // regras acima passam a mentir.
    const on = () => setResolved(apply('system'));
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [theme]);

  const value = useMemo<ThemeState>(
    () => ({
      theme,
      resolved,
      setTheme: setThemeState,
      toggle: () => setThemeState(resolved === 'dark' ? 'light' : 'dark'),
    }),
    [theme, resolved],
  );

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme(): ThemeState {
  const v = useContext(ThemeCtx);
  if (!v) throw new Error('useTheme precisa de <ThemeProvider>');
  return v;
}
